import type { Hint } from "../game/hints";
import "./HintView.css";

interface Props {
  hint: Hint;
}

/**
 * Draws a hint: a number line with the known numbers marked and a jump to
 * count along, or an array of dots to count. Never shows the answer.
 */
export function HintView({ hint }: Props) {
  if (hint.kind === "array") return <DotArray rows={hint.rows} cols={hint.cols} />;
  return <NumberLine hint={hint} />;
}

function NumberLine({ hint }: { hint: Hint & { kind: "line" } }) {
  const { from, to, marks, jump } = hint;
  const span = to - from;
  const W = 1000;
  const H = 150;
  const pad = 36;
  const x = (n: number) => pad + ((n - from) / span) * (W - 2 * pad);
  const baseline = 92;
  // Label every tick on a short line, every ten on a long one.
  const labelEvery = span <= 20 ? 1 : span <= 40 ? 5 : 10;
  const ticks = Array.from({ length: span + 1 }, (_, i) => from + i);
  const landing = jump ? jump.from + jump.by : null;
  const text = jump
    ? `Number line from ${from} to ${to}, ${marks.join(" and ")} marked, a jump of ${Math.abs(jump.by)} ${jump.by < 0 ? "back" : "on"}.`
    : `Number line from ${from} to ${to}, ${marks.join(" and ")} marked.`;
  return (
    <svg className="hint hint-line" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={text}>
      <line className="hl-axis" x1={pad - 10} x2={W - pad + 10} y1={baseline} y2={baseline} />
      {ticks.map((n) => {
        const big = n % labelEvery === 0;
        return (
          <g key={n}>
            <line
              className={"hl-tick" + (big ? " big" : "")}
              x1={x(n)}
              x2={x(n)}
              y1={baseline - (big ? 14 : 8)}
              y2={baseline + (big ? 14 : 8)}
            />
            {big && (
              <text className="hl-num" x={x(n)} y={baseline + 40} textAnchor="middle">
                {n}
              </text>
            )}
          </g>
        );
      })}
      {jump && landing !== null && (
        <path
          className="hl-jump"
          d={`M${x(jump.from)} ${baseline - 18} Q${(x(jump.from) + x(landing)) / 2} ${baseline - 90} ${x(landing)} ${baseline - 18}`}
        />
      )}
      {jump && landing !== null && (
        <text
          className="hl-by display"
          x={(x(jump.from) + x(landing)) / 2}
          y={baseline - 62}
          textAnchor="middle"
        >
          {jump.by < 0 ? `− ${-jump.by}` : `+ ${jump.by}`}
        </text>
      )}
      {landing !== null && <circle className="hl-land" cx={x(landing)} cy={baseline} r={9} />}
      {marks.map((m) => (
        <g key={m}>
          <circle className="hl-mark" cx={x(m)} cy={baseline} r={13} />
          <text className="hl-mark-num display" x={x(m)} y={baseline - 26} textAnchor="middle">
            {m}
          </text>
        </g>
      ))}
    </svg>
  );
}

function DotArray({ rows, cols }: { rows: number; cols: number }) {
  const gap = 4;
  const cell = 18;
  const W = cols * cell + (cols - 1) * gap;
  const H = rows * cell + (rows - 1) * gap;
  // Dots come in fives so they're quicker to count.
  const dots: { r: number; c: number }[] = [];
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) dots.push({ r, c });
  return (
    <svg
      className="hint hint-array"
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={`${rows} rows of ${cols} dots`}
      style={{ aspectRatio: `${W} / ${H}` }}
    >
      {dots.map(({ r, c }) => (
        <circle
          key={`${r}-${c}`}
          className={"ha-dot" + (Math.floor(c / 5) % 2 === 1 ? " alt" : "")}
          cx={c * (cell + gap) + cell / 2}
          cy={r * (cell + gap) + cell / 2}
          r={cell / 2 - 1}
        />
      ))}
    </svg>
  );
}
